<template>
  <div class="space-y-6">
    <!-- List View -->
    <div v-if="state === 'list'" class="space-y-6">
      <div v-if="items.length > 0" class="space-y-3">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="flex items-center gap-4 p-4 rounded-2xl border border-border/40 bg-card hover:bg-muted/10 transition-all group"
        >
          <!-- Item Thumbnail / Icon -->
          <div
            class="w-14 h-14 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden"
          >
            <slot name="item-icon" :item="item">
              <Briefcase class="w-6 h-6 text-muted-foreground/50" />
            </slot>
          </div>

          <!-- Item Details -->
          <div class="flex-1 min-w-0">
            <h4 class="text-[16px] font-bold text-foreground truncate">
              {{ getItemTitle(item) }}
            </h4>
            <p class="text-[13px] text-muted-foreground truncate">
              {{ getItemSubtitle(item) }}
            </p>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1">
            <button
              @click="editItem(index)"
              class="p-2 text-muted-foreground hover:text-primary transition-colors"
            >
              <Pencil class="w-4 h-4" />
            </button>
            <button
              @click="confirmDelete(index)"
              class="p-2 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Add Another Button -->
      <button
        @click="
          state = 'form';
          currentItem = {};
          editingIndex = -1;
        "
        class="w-fit flex items-center gap-2 px-6 py-2.5 border border-border rounded-full text-sm font-semibold hover:bg-muted transition-all"
      >
        Add another
      </button>
    </div>

    <!-- Form View -->
    <div v-else-if="state === 'form'" class="space-y-6 pb-4">
      <slot name="form" :item="currentItem" :update="updateCurrentItem"></slot>
    </div>

    <!-- Delete Confirmation View -->
    <div v-else-if="state === 'delete'" class="space-y-8 py-4">
      <div class="flex items-center gap-4">
        <button
          @click="state = 'list'"
          class="p-2 rounded-full border border-border hover:bg-muted"
        >
          <ArrowLeft class="w-5 h-5" />
        </button>
        <h3 class="text-2xl font-bold">Remove {{ itemName }}</h3>
      </div>

      <p class="text-muted-foreground text-center py-4">
        Are you sure you want to remove this {{ itemName.toLowerCase() }} from
        your profile?
      </p>

      <div class="flex justify-center gap-4">
        <button
          class="px-10 py-3 border border-border rounded-full font-semibold hover:bg-muted transition-all"
          @click="state = 'list'"
        >
          Keep
        </button>
        <button
          class="px-10 py-3 bg-destructive text-destructive-foreground rounded-full font-semibold hover:opacity-90 transition-all"
          @click="removeItem"
        >
          Remove
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Briefcase, Pencil, Trash2, ArrowLeft } from "lucide-vue-next";

export default {
  name: "SectionListManager",
  components: {
    Briefcase,
    Pencil,
    Trash2,
    ArrowLeft,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    itemName: {
      type: String,
      default: "item",
    },
    titleKey: {
      type: String,
      default: "title",
    },
    subtitleKey: {
      type: String,
      default: "subtitle",
    },
  },
  emits: [
    "update:items",
    "state-change",
    "add-item",
    "update-item",
    "remove-item",
  ],
  data() {
    return {
      state: "list", // list, form, delete
      currentItem: {},
      editingIndex: -1,
      deletingIndex: -1,
    };
  },
  watch: {
    state(newState) {
      this.$emit("state-change", newState);
    },
  },
  methods: {
    getItemTitle(item) {
      return item[this.titleKey] || item.name || "Untitled";
    },
    getItemSubtitle(item) {
      return item[this.subtitleKey] || item.company || item.school || "";
    },
    editItem(index) {
      this.editingIndex = index;
      // Use structuredClone to preserve File objects and avoid prototype issues
      try {
        this.currentItem = structuredClone(this.items[index]);
      } catch (e) {
        // Fallback for older browsers or non-serializable objects
        this.currentItem = JSON.parse(JSON.stringify(this.items[index]));
      }
      this.state = "form";
    },
    confirmDelete(index) {
      this.deletingIndex = index;
      this.state = "delete";
    },
    saveItem() {
      if (this.editingIndex >= 0) {
        this.$emit("update-item", {
          item: this.currentItem,
          index: this.editingIndex,
        });
      } else {
        this.$emit("add-item", this.currentItem);
      }
    },
    // Finalize the local list after parent API call success
    commitSave() {
      const newItems = [...this.items];
      if (this.editingIndex >= 0) {
        newItems[this.editingIndex] = this.currentItem;
      } else {
        newItems.push(this.currentItem);
      }
      this.$emit("update:items", newItems);
      this.state = "list";
      this.currentItem = {};
      this.editingIndex = -1;
    },
    cancelForm() {
      this.state = "list";
      this.currentItem = {};
      this.editingIndex = -1;
    },
    removeItem() {
      this.$emit("remove-item", {
        item: this.items[this.deletingIndex],
        index: this.deletingIndex,
      });
    },
    commitRemove() {
      const newItems = [...this.items];
      newItems.splice(this.deletingIndex, 1);
      this.$emit("update:items", newItems);
      this.state = "list";
      this.deletingIndex = -1;
    },
    updateCurrentItem(updates) {
      this.currentItem = { ...this.currentItem, ...updates };
    },
  },
};
</script>
